import React from 'react';
import { Box, Typography } from '@mui/material';
import {
  Favorite as HeartIcon,
  MonitorHeart as PulseIcon,
  Thermostat as TempIcon,
  Air as RespIcon,
  Scale as WeightIcon,
  Height as HeightIcon,
} from '@mui/icons-material';
import { tempUnit } from '../utils/temperature';

/**
 * Compact read-only strip of a patient's vitals.
 *
 * Accepts either shape the API returns: the patient payload (`temperature_value`,
 * `last_vitals_recorded_at`) or a dated snapshot from the vitals/visit-history
 * endpoints (`temperature`, `recorded_at`).
 */

const has = (v) => v !== null && v !== undefined && v !== '';

export function vitalsFields(v) {
  if (!v) return [];
  // The patient payload's `temperature` is a display string ("36.8°C", or "N/A" when
  // there is none) — only a number counts, or "N/A" was shown with a unit after it.
  const parsed = parseFloat(v.temperature);
  const temperature = has(v.temperature_value) ? v.temperature_value
    : (Number.isFinite(parsed) ? parsed : null);
  return [
    {
      key: 'bp',
      label: 'Blood pressure',
      icon: HeartIcon,
      value:
        has(v.bp_systolic) && has(v.bp_diastolic)
          ? `${v.bp_systolic}/${v.bp_diastolic}`
          : null,
      unit: 'mmHg',
    },
    { key: 'pulse', label: 'Pulse', icon: PulseIcon, value: has(v.pulse) ? v.pulse : null, unit: 'bpm' },
    {
      key: 'temp',
      label: 'Temperature',
      icon: TempIcon,
      value: has(temperature) ? temperature : null,
      unit: has(temperature) ? tempUnit(temperature) : '',
    },
    {
      key: 'rr',
      label: 'Resp. rate',
      icon: RespIcon,
      value: has(v.respiratory_rate) ? v.respiratory_rate : null,
      unit: 'rpm',
    },
    { key: 'weight', label: 'Weight', icon: WeightIcon, value: has(v.weight_kg) ? v.weight_kg : null, unit: 'kg' },
    { key: 'height', label: 'Height', icon: HeightIcon, value: has(v.height_cm) ? v.height_cm : null, unit: 'cm' },
  ];
}

export function hasAnyVitals(v) {
  return vitalsFields(v).some((f) => f.value !== null);
}

export default function VitalsSummary({
  vitals,
  showRecordedAt = true,
  emptyText = 'No vitals recorded yet.',
  sx = {},
}) {
  const fields = vitalsFields(vitals);
  const recordedAt = vitals?.last_vitals_recorded_at || vitals?.recorded_at || null;

  if (!hasAnyVitals(vitals)) {
    return (
      <Typography variant="body2" color="text.secondary" sx={sx}>
        {emptyText}
      </Typography>
    );
  }

  return (
    <Box sx={sx}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {fields.map(({ key, label, icon: Icon, value, unit }) => (
          <Box
            key={key}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 0.75,
              borderRadius: 2,
              bgcolor: 'grey.50',
              border: '1px solid rgba(4, 47, 46, 0.08)',
              minWidth: 128,
            }}
          >
            <Icon fontSize="small" color={value === null ? 'disabled' : 'primary'} />
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
                sx={{ lineHeight: 1.2 }}
              >
                {label}
              </Typography>
              <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.3 }}>
                {value === null ? '—' : `${value} ${unit}`}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>
      {showRecordedAt && recordedAt && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
          Recorded{' '}
          {new Date(recordedAt).toLocaleString(undefined, {
            dateStyle: 'medium',
            timeStyle: 'short',
          })}
        </Typography>
      )}
    </Box>
  );
}
